import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { FEATURE_FLAGS } from 'constants/security'
import { Nursery } from 'services/nurseries/models'

import auth from 'utils/auth'
import { generateRoute } from 'utils/routing'

import { withAppService, withAppServiceProps } from 'services/app'
import appSelectors from 'services/app/selectors'
import { withNurseryContextService, withNurseryContextServiceProps } from 'services/nurseryContext'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import { Typography } from 'components'
import { ModalType } from 'modals'
import ManagementPaymentGateway from 'module/Management/ManagementFinanceSettings/ManagementPaymentGateway'

import i18n from 'translations'

import DetailView from './DetailView'
import { MarketPlaceProps, ROUTE_PATH_MAPPING } from '../AppMarketplaceConstants'

const NURSERY_SETTINGS_GROUPS = {
  read: [
    'nurseryIntegration',
    'nurseryIntegrations.stripe',
  ],
}

const mapState = (state, { params }) => ({
  ManagementPaymentGatewayGranted: auth.SELECTORS.getComponentIsAuthorised(state, ManagementPaymentGateway),
  mobilePaymentsIsEnabled: auth.SELECTORS.getIsAuthorised(state, {
    flags: [FEATURE_FLAGS.MOBILE_PAYMENTS],
  }),
  nurseryOptions: appSelectors.getContextNurseryRouterConfig(state, params),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

type DetailContainerProps = PropsFromRedux
  & withAppServiceProps
  & withModalServiceProps
  & withNurseryContextServiceProps
  & withRouterProps

const DetailContainer: React.FC<DetailContainerProps> = ({
  ManagementPaymentGatewayGranted,
  mobilePaymentsIsEnabled,
  modalActions,
  navigate,
  nurseryContextActions,
  nurseryOptions,
  routes,
}) => {
  const route = routes[routes.length - 1]
  const appMarketplaceData: MarketPlaceProps = ROUTE_PATH_MAPPING[route.path]

  const [isBePaidFetching, setIsBePaidFetching] = useState<boolean>(
    'be-paid' === appMarketplaceData?.appType && ManagementPaymentGatewayGranted,
  )
  const [nurseryData, setNurseryData] = useState<Nursery>()

  useEffect(() => {
    if ('be-paid' === appMarketplaceData.appType && mobilePaymentsIsEnabled) {
      nurseryContextActions.getSettings({
        onSuccess: (response) => {
          setNurseryData(response?.data)
          setIsBePaidFetching(false)
        },
        onlyData: true,
        params: [nurseryOptions.id, {
          groups: NURSERY_SETTINGS_GROUPS,
        }],
      })
    }
  }, [])

  const handleRedirectToPaymentGateway = () => {
    if (!ManagementPaymentGatewayGranted) {
      modalActions.show<ModalType.ALERT>(ModalType.ALERT, {
        icon: 'warning',
        text: (
          <div>
            <Typography fontSize={22} margin="0 0 10px" bold>
              {i18n.t('module:AppMarketplace:BePaid:permissionAlertTitle')}
            </Typography>
            <Typography fontSize={18}>
              {i18n.t('module:AppMarketplace:BePaid:permissionAlertContent')}
            </Typography>
          </div>
        ),
      })

      return
    }

    navigate(generateRoute('MANAGEMENT.FINANCE_SETTINGS.PAYMENT_GATEWAY'))
  }

  return (
    <DetailView
      appMarketplaceData={appMarketplaceData}
      isBePaidFetching={isBePaidFetching}
      mobilePaymentsIsEnabled={mobilePaymentsIsEnabled}
      nurseryData={nurseryData}
      onBePaidConnectClick={handleRedirectToPaymentGateway}
      onBePaidManageClick={handleRedirectToPaymentGateway}
    />
  )
}

const enhance = compose(
  withAppService,
  withRouter,
  withModalService,
  withNurseryContextService,
  connector,
)

export default enhance(DetailContainer)
